import { Injectable, inject } from '@angular/core';
import type { SupabaseClient } from '@supabase/supabase-js';
import { classifyEanQuery } from '../../../shared/utils/ean.util';
import { AuthStore } from '../../auth.store';
import { type ChunkProgressReporter, reportChunkProgress } from '../shared/chunk-progress';
import { likeLiteral, postgrestQuote } from '../shared/postgrest-filter';
import {
  type CatalogPage,
  type CatalogProduct,
  type CatalogScope,
  type CatalogSearch,
  type CategoryBrandCountsByPath,
  CoverageCatalogRepository,
  type ProductCategoryPaths,
} from './coverage-catalog-repository';

const PRODUCT_TABLE = 'medpdb2';
const PRODUCT_SELECT = 'EAN, name, brand, category, prio';
const BRAND_TABLE = 'brandb';
const REFERENCE_TABLE = 'portal_references';
const COUNTS_REFERENCE_KEY = 'ean_w_data_counts_per_category_brand';
const AVAILABLE_BRANDS_REFERENCE_KEY = 'available_brands';

/**
 * EANs per URL when querying by EAN. Matches the observation repository's own
 * batch size, and keeps the request line well short of any proxy limit.
 */
const EAN_CHUNK = 200;

/**
 * Brand slugs per URL when querying by brand. An org's entitlement is a
 * handful, but a super admin's scope is every portal brand, around 1,500.
 */
const BRAND_CHUNK = 200;

/** Rows one brand-in-category drill-down reads at most. */
const BRAND_CATEGORY_LIMIT = 200;

const EMPTY_PAGE: CatalogPage = { products: [], truncated: false };

type ProductRow = {
  readonly EAN: string;
  readonly name: string | null;
  readonly brand: string | null;
  readonly category: string | null;
  readonly prio: number | null;
};

type PathRow = {
  readonly EAN: string;
  readonly category: string | null;
  readonly altcategory: string | null;
};

type BrandRow = { readonly brandname: string | null; readonly brandalias: string | null };

/** One access path into the catalog: entitled brands, or specific EANs. */
type ScopeLeg = {
  readonly column: 'brand_slug' | 'EAN';
  readonly values: readonly string[];
};

const toProduct = (row: ProductRow): CatalogProduct => ({
  ean: row.EAN,
  name: row.name,
  brand: row.brand,
  category: row.category,
  prio: row.prio,
});

const byPrioDesc = (a: CatalogProduct, b: CatalogProduct): number =>
  (b.prio ?? -Infinity) - (a.prio ?? -Infinity);

/**
 * The org can reach a product two ways: its brand is entitled, or it sits in
 * one of the org's category pseudobrands. Both are legs of the same access
 * universe; the EAN legs are chunked to keep each URL bounded.
 */
function scopeLegs(scope: CatalogScope): readonly ScopeLeg[] {
  const legs: ScopeLeg[] = [];
  for (let i = 0; i < scope.brandSlugs.length; i += BRAND_CHUNK) {
    legs.push({ column: 'brand_slug', values: scope.brandSlugs.slice(i, i + BRAND_CHUNK) });
  }
  for (let i = 0; i < scope.eans.length; i += EAN_CHUNK) {
    legs.push({ column: 'EAN', values: scope.eans.slice(i, i + EAN_CHUNK) });
  }
  return legs;
}

/**
 * The term reduced to its words and rejoined with wildcards, so "omega-3"
 * matches "Omega 3", "Omega-3" and "Omega3" alike. Word order is significant,
 * which matches how the term is typed. LIKE metacharacters and the characters
 * that would break out of the or() expression are stripped rather than
 * escaped, so no input can widen the filter or add filters of its own.
 */
function wildcardPattern(term: string): string | null {
  const words = term
    .replace(/[%_"\\,()]/g, ' ')
    .trim()
    .split(/[\s-]+/)
    .filter(Boolean);
  return words.length === 0 ? null : `"%${words.join('%')}%"`;
}

/**
 * The or() clauses a search matches on. The EAN leg goes through the same
 * classifier the consumer search uses, so a pasted real EAN is masked to the
 * stored form; a term that is not a long enough digit run contributes none.
 */
function searchClauses(search: CatalogSearch, pattern: string): readonly string[] {
  const { fields, term } = search;
  const clauses: string[] = [];
  if (fields.name) clauses.push(`name.ilike.${pattern}`);
  if (fields.brand) clauses.push(`brand.ilike.${pattern}`);
  if (fields.tags) clauses.push(`tags.ilike.${pattern}`);
  if (fields.desc) clauses.push(`desc.ilike.${pattern}`);
  if (fields.category) {
    clauses.push(`category.ilike.${pattern}`, `altcategory.ilike.${pattern}`);
  }
  if (fields.ean) {
    const classified = classifyEanQuery(term);
    if (classified.maskedTerm) clauses.push(`EAN.like."${classified.maskedTerm}%"`);
  }
  return clauses;
}

/**
 * Repeated or() params are ANDed by PostgREST, so a vendor leg narrows the
 * field match rather than widening it. A product lists its vendors in the
 * `offers` jsonb, one element per vendor keyed `apoid`, so each vendor is one
 * containment clause (`offers @> '[{"apoid": "<slug>"}]'`), quoted for the or() grammar.
 */
function vendorClause(vendorSlugs: readonly string[]): string | null {
  if (vendorSlugs.length === 0) return null;
  return vendorSlugs
    .map((slug) => `offers.cs.${postgrestQuote(JSON.stringify([{ apoid: slug }]))}`)
    .join(',');
}

@Injectable()
export class SupabaseCoverageCatalogRepository extends CoverageCatalogRepository {
  private readonly authStore = inject(AuthStore);

  private client(): Promise<SupabaseClient> {
    return this.authStore.getSupabaseClient();
  }

  async brandLabels(): Promise<Readonly<Record<string, string>>> {
    const supabase = await this.client();
    const { data, error } = await supabase.from(BRAND_TABLE).select('brandname, brandalias');
    if (error) throw error;
    const labels: Record<string, string> = {};
    for (const row of (data ?? []) as BrandRow[]) {
      const slug = String(row.brandname ?? '').toLowerCase();
      if (slug) labels[slug] = String(row.brandalias ?? row.brandname ?? '');
    }
    return labels;
  }

  async availableBrands(): Promise<readonly string[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(REFERENCE_TABLE)
      .select('reference_data')
      .eq('reference_key', AVAILABLE_BRANDS_REFERENCE_KEY)
      .maybeSingle();
    if (error) throw error;
    const stored = Array.isArray(data?.reference_data) ? (data.reference_data as unknown[]) : [];
    const slugs = new Set<string>();
    for (const value of stored) {
      const slug = String(value ?? '')
        .trim()
        .toLowerCase();
      if (slug) slugs.add(slug);
    }
    return [...slugs].sort();
  }

  async categoryBrandCounts(): Promise<CategoryBrandCountsByPath> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(REFERENCE_TABLE)
      .select('reference_data')
      .eq('reference_key', COUNTS_REFERENCE_KEY)
      .maybeSingle();
    if (error) throw error;
    return (data?.reference_data ?? {}) as CategoryBrandCountsByPath;
  }

  async categoryPathsOf(
    eans: readonly string[],
    onProgress?: ChunkProgressReporter
  ): Promise<readonly ProductCategoryPaths[]> {
    if (eans.length === 0) return [];
    const supabase = await this.client();
    const paths: ProductCategoryPaths[] = [];
    for (let i = 0; i < eans.length; i += EAN_CHUNK) {
      const { data, error } = await supabase
        .from(PRODUCT_TABLE)
        .select('EAN, category, altcategory')
        .in('EAN', eans.slice(i, i + EAN_CHUNK))
        // One row per EAN asked for, so this bounds the read without ever
        // cutting a legitimate result short.
        .limit(EAN_CHUNK);
      if (error) throw error;
      for (const row of (data ?? []) as PathRow[]) {
        paths.push({ ean: row.EAN, category: row.category, altcategory: row.altcategory });
      }
      reportChunkProgress(onProgress, i + EAN_CHUNK, eans.length);
    }
    return paths;
  }

  /**
   * Matches category OR altcategory, the same pair compileSpec uses for
   * category-under. Matching category alone silently drops every product filed
   * under an alternate category.
   */
  productsUnderPath(scope: CatalogScope, categoryPath: string, cap: number): Promise<CatalogPage> {
    // Quoted so a path containing a comma cannot split the or() expression into extra
    // filters; escaped so its underscores match as separators, not single-character wildcards.
    const pattern = postgrestQuote(`${likeLiteral(categoryPath)}%`);
    return this.scopedQuery(
      scope,
      `category.like.${pattern},altcategory.like.${pattern}`,
      null,
      cap
    );
  }

  searchProducts(scope: CatalogScope, search: CatalogSearch, cap: number): Promise<CatalogPage> {
    const pattern = wildcardPattern(search.term);
    if (pattern === null) return Promise.resolve(EMPTY_PAGE);
    const clauses = searchClauses(search, pattern);
    if (clauses.length === 0) return Promise.resolve(EMPTY_PAGE);
    return this.scopedQuery(scope, clauses.join(','), vendorClause(search.vendorSlugs), cap);
  }

  async productsByEan(eans: readonly string[]): Promise<readonly CatalogProduct[]> {
    if (eans.length === 0) return [];
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(PRODUCT_TABLE)
      .select(PRODUCT_SELECT)
      .in('EAN', [...eans]);
    if (error) throw error;
    return ((data ?? []) as ProductRow[]).map(toProduct);
  }

  async productsOfBrandUnderPath(
    brandSlug: string,
    categoryPath: string
  ): Promise<readonly CatalogProduct[]> {
    const supabase = await this.client();
    const { data, error } = await supabase
      .from(PRODUCT_TABLE)
      .select(PRODUCT_SELECT)
      .eq('brand_slug', brandSlug)
      .like('category', `${likeLiteral(categoryPath)}%`)
      .limit(BRAND_CATEGORY_LIMIT);
    if (error) throw error;
    return ((data ?? []) as ProductRow[]).map(toProduct);
  }

  /**
   * Runs one query per access leg and merges the results.
   *
   * The two legs are two different columns. They cannot go in one or() without
   * putting every pseudobrand EAN in the URL, so each leg is its own request,
   * and a long brand or EAN list is split over several legs of the same column.
   * Legs overlap freely; the merge dedupes by EAN. Every leg carries the same
   * match and vendor filters and the same cap, so no leg can crowd the others
   * out of the result. Fetches cap + 1 rows per leg so a complete result can
   * be told from a truncated one.
   */
  private async scopedQuery(
    scope: CatalogScope,
    matchOr: string,
    vendorOr: string | null,
    cap: number
  ): Promise<CatalogPage> {
    const legs = scopeLegs(scope);
    if (legs.length === 0) return EMPTY_PAGE;

    const supabase = await this.client();
    const results = await Promise.all(
      legs.map(async (leg) => {
        let query = supabase
          .from(PRODUCT_TABLE)
          .select(PRODUCT_SELECT)
          .in(leg.column, [...leg.values])
          .or(matchOr);
        if (vendorOr !== null) query = query.or(vendorOr);
        const { data, error } = await query
          .order('prio', { ascending: false, nullsFirst: false })
          .limit(cap + 1);
        if (error) throw error;
        return ((data ?? []) as ProductRow[]).map(toProduct);
      })
    );

    const byEan = new Map<string, CatalogProduct>();
    let truncated = false;
    for (const products of results) {
      if (products.length > cap) truncated = true;
      for (const product of products) {
        if (!byEan.has(product.ean)) byEan.set(product.ean, product);
      }
    }

    // Each leg came back prio-ordered, but merging interleaves them, so restore
    // the order the callers rely on.
    const merged = [...byEan.values()].sort(byPrioDesc);
    if (merged.length > cap) truncated = true;
    return { products: merged, truncated };
  }
}
